<template>
  <div></div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useStateStore } from "@/store/state.store";
import { useLayoutStore } from "@/store/layout.store";
import { useHomeLegacyStore } from "@/store/home-legacy.store";

const props = defineProps<{
  steps?: unknown[];
  type?: string;
}>();

const stateStore = useStateStore();
const layoutStore = useLayoutStore();
const homeLegacyStore = useHomeLegacyStore();

const driverObj = ref<unknown>(null);

const getIsMobile = computed(() => layoutStore.getIsMobile);

function startTourTopBar() {
  const localSteps = getIsMobile.value
    ? (props.steps || []).map((step) => ({ ...step, element: null }))
    : [...(props.steps || [])];

  driverObj.value = driver({
    popoverClass: "driverjs-theme",
    overlayColor: "#454545",
    overlayOpacity: 0.8,
    showProgress: true,
    allowClose: false,
    progressText: "{{current}} de {{total}}",
    showButtons: ["next", "previous", "close"],
    steps: localSteps,
    onDestroyed: () => {
      const topbarUserMenu = document.querySelector(".user-popup");
      const topbarUser = document.querySelector(".user-menu");
      if (topbarUserMenu && (topbarUserMenu as HTMLElement).style.display !== "none") {
        (topbarUser as HTMLElement)?.click();
      }
      homeLegacyStore.saveTourGuideStatus(false).catch(() => {});
    },
  });

  driverObj.value.drive();
}

function startTour() {
  const localSteps = getIsMobile.value
    ? (props.steps || []).map((step) => ({ ...step, element: null }))
    : [...(props.steps || [])];

  driverObj.value = driver({
    popoverClass: "driverjs-theme",
    overlayColor: "#454545",
    overlayOpacity: 0.8,
    showProgress: true,
    allowClose: false,
    progressText: "{{current}} de {{total}}",
    showButtons: ["next", "previous", "close"],
    steps: localSteps,
    onDestroyed: () => {
      const topbarUserMenu = document.querySelector(".user-popup");
      const topbarUser = document.querySelector(".user-menu");
      if (topbarUserMenu && (topbarUserMenu as HTMLElement).style.display !== "none") {
        (topbarUser as HTMLElement)?.click();
      }
      homeLegacyStore.saveTourGuideStatus(false).catch(() => {});
    },
  });

  checkTourGuideStatus()
    .then((shouldShow) => {
      if (!shouldShow) return;

      if (props.type === "admin" && shouldShowForAdmin()) {
        driverObj.value.drive();
      } else if ((props.type === "student" || !props.type) && shouldShowForStudent()) {
        driverObj.value.drive();
      }
    })
    .catch(() => {});
}

function moveNext() {
  driverObj.value?.moveNext();
}

function movePrevious() {
  driverObj.value?.movePrevious();
}

function shouldShowForStudent(): boolean {
  return stateStore.role === "student";
}

function shouldShowForAdmin(): boolean {
  return stateStore.role === "admin" || stateStore.role === "owner";
}

async function checkTourGuideStatus(): Promise<boolean> {
  try {
    const status = await homeLegacyStore.fetchTourGuideStatus();
    if (Array.isArray(status) && status.length === 0) return true;
    return status["tour-guide"] !== "0";
  } catch {
    return false;
  }
}

defineExpose({
  startTour,
  startTourTopBar,
  moveNext,
  movePrevious,
});

onMounted(() => {
  if ((props.steps || []).length > 0) {
    startTour();
  }
});
</script>

<style lang="scss">
.driver-popover.driverjs-theme {
  display: flex !important;
  max-width: 441px;
  padding: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 1px 8px #0003;

  [data-theme="dark"] & {
    background-color: #000;
  }
}

.driver-popover.driverjs-theme .driver-popover-title {
  color: #141416;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 28.8px;
  margin-bottom: 12px !important;

  [data-theme="dark"] & {
    color: #fff;
  }
}

.driver-popover.driverjs-theme .driver-popover-description {
  width: auto;
  color: #6E6E71;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  margin-bottom: 24px;

  [data-theme="dark"] & {
    color: #B5B5B5;
  }
}

.driver-popover.driverjs-theme .driver-popover-close-btn {
  all: unset;
  position: absolute;
  top: 10px;
  right: 10px;
  width: 18px;
  height: 18px;
  font-weight: normal;
  font-size: 25px;
  text-align: center;
  color: #141416;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.2s ease;
  z-index: 10;
  margin-top: 10px;
  margin-right: 10px;
  padding-left: 1px;
  padding-bottom: 1px;

  [data-theme="dark"] & {
    background-color: #000;
    color: #fff;
  }
}

.driver-popover-navigation-btns .driver-popover-next-btn {
  display: flex;
  height: 40px;
  padding: 0px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: var(--maincolor);
  color: #fff;
  text-shadow: none;
  float: right;
  border: none;
}

.driver-popover-navigation-btns .driver-popover-prev-btn {
  display: flex;
  height: 40px;
  padding: 0px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: var(--maincolor);
  border-radius: 8px;
  text-shadow: none;
  float: right;
  border: 1px solid var(--maincolor);
  margin-right: 10px;

  [data-theme="dark"] & {
    background-color: transparent;
    color: #fff;
    border: 1px solid #878787;
  }
}

.driver-popover-navigation-btns .driver-popover-next-btn:hover {
  background: var(--maincolor);
  color: #fff;
  opacity: 0.8;
}

.driver-popover-footer {
  width: 100%;
}

.driver-popover-footer span {
  color: #6E6E71;

  [data-theme="dark"] & {
    color: #B5B5B5;
  }

  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
}

.driver-popover-footer button {
  color: #FFF;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 16.8px;
  border: none;
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-left.driver-popover-arrow {
  border-left-color: #fff;

  [data-theme="dark"] & {
    border-left-color: #000;
  }
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-right.driver-popover-arrow {
  border-right-color: #fff;

  [data-theme="dark"] & {
    border-right-color: #000;
  }
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-top.driver-popover-arrow {
  border-top-color: #fff;

  [data-theme="dark"] & {
    border-top-color: #000;
  }
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-bottom.driver-popover-arrow {
  border-bottom-color: #fff;

  [data-theme="dark"] & {
    border-bottom-color: #000;
  }
}

@media (max-width: 768px) {
  .driver-popover.driverjs-theme {
    max-width: 94%;
    padding: 20px;
  }
}
</style>
